import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Brain } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import { Emotion } from '../data/emotions';
import { generateAnalysisReport, StreamCallbacks } from '../services/analysis';
import { generateReferralTestCodes, isReferralEnabled } from '../services/codeVerification';
import { statsStorage } from '../services/persistentStorage';

interface LocationState {
  leastEmotions: Emotion[];
  mostEmotions: Emotion[];
  analysisReport: string;
  isStreaming?: boolean;
}

const AnalysisReport: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const state = location.state as LocationState;
  const [showCursor, setShowCursor] = useState<boolean>(true); // 控制光标显示
  // 流式生成相关状态
  const [streamContent, setStreamContent] = useState<string>('');
  const [isStreaming, setIsStreaming] = useState<boolean>(state?.isStreaming || false);
  // 推荐功能相关状态
  const [showReferralButton, setShowReferralButton] = useState<boolean>(false);
  const [referralCodes, setReferralCodes] = useState<string[]>([]);
  const [isGeneratingCodes, setIsGeneratingCodes] = useState<boolean>(false);
  // 移除未使用的formattedContent状态

  
  // 计算要显示的内容
  // 在这里不定义displayContent，它将在下面定义
  
  // 使用大语魔的译书字体

  // 处理流式生成
  useEffect(() => {
    // 如果有初始内容，先设置它
    if (state?.analysisReport) {
      setStreamContent(state.analysisReport);
    }
    
    // 只有当isStreaming为true时才发起新请求
    // 这样当从选择页面跳转过来时，如果isStreaming为false，就不会重新发起请求
    if (state?.isStreaming && state.leastEmotions && state.mostEmotions) {
      const callbacks: StreamCallbacks = {
        onStart: () => {
          console.log('开始在报告页接收流式响应');
          setStreamContent('');
          setIsStreaming(true);
          setShowCursor(true);
        },
        onToken: (token) => {
          setStreamContent(prev => prev + token);
        },
        onComplete: async (fullText) => {
          console.log('流式响应完成');
          setStreamContent(fullText);
          setIsStreaming(false);
          setShowCursor(false);
          
          // 记录测评完成次数
          try {
            await statsStorage.recordCompletion('emotional-memory-pattern');
            console.log('测评完成次数已记录');
          } catch (error) {
            console.error('记录测评完成失败:', error);
          }
          
          // 检查是否显示推荐按钮
          isReferralEnabled().then(enabled => {
            if (enabled) {
              setShowReferralButton(true);
            }
          }).catch(error => {
            console.error('检查推荐功能状态失败:', error);
          });
          
          setTimeout(() => {
            const letterPaper = document.querySelector('.letter-paper');
            if (letterPaper) {
              const rect = letterPaper.getBoundingClientRect();
              const scrollTop = window.pageYOffset + rect.top - 80;
              
              window.scrollTo({
                top: scrollTop,
                behavior: 'auto'
              });
              
              setTimeout(() => {
                window.scrollTo({
                  top: scrollTop,
                  behavior: 'auto'
                });
              }, 100);
            }
          }, 300);
        },
        onError: (error) => {
          console.error('流式响应出错:', error);
          setIsStreaming(false);
          setShowCursor(false);
        }
      };
      
      generateAnalysisReport({
        leastEmotions: state.leastEmotions,
        mostEmotions: state.mostEmotions
      }, callbacks).catch(error => {
        console.error('生成分析报告失败:', error);
        setIsStreaming(false);
        setShowCursor(false);
      });
    } else {
      // 如果不需要流式生成，但有初始内容，则显示初始内容并设置为非流式状态
      if (state?.analysisReport) {
        // 即使不是流式模式，也显示光标效果，以便用户知道内容正在生成
        setIsStreaming(true);
        setShowCursor(true);
      }
    }
  }, [state]);
  
  // 添加监听window.postMessage的事件处理器，接收从选择页面发送的新token和完成通知
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      // 确保消息来源是当前窗口
      if (event.source !== window) return;
      
      // 处理NEW_TOKEN类型的消息
      if (event.data && event.data.type === 'NEW_TOKEN') {
        const token = event.data.token;
        console.log('报告页面收到新token:', token.substring(0, 20) + '...');
        
        // 更新流式内容
        setStreamContent(prev => prev + token);
      }
      
      // 处理STREAM_COMPLETE类型的消息
      if (event.data && event.data.type === 'STREAM_COMPLETE') {
        console.log('报告页面收到流式生成完成通知');
        const fullText = event.data.fullText;
        
        // 更新最终内容
        setStreamContent(fullText);
        setIsStreaming(false);
        setShowCursor(false);
        
        // 记录测评完成次数
        (async () => {
          try {
            await statsStorage.recordCompletion('emotional-memory-pattern');
            console.log('测评完成次数已记录');
          } catch (error) {
            console.error('记录测评完成失败:', error);
          }
        })();
        
        // 检查是否显示推荐按钮
        isReferralEnabled().then(enabled => {
          if (enabled) {
            setShowReferralButton(true);
          }
        }).catch(error => {
          console.error('检查推荐功能状态失败:', error);
        });
        
        // 滚动到适当位置
        setTimeout(() => {
          const letterPaper = document.querySelector('.letter-paper');
          if (letterPaper) {
            const rect = letterPaper.getBoundingClientRect();
            const scrollTop = window.pageYOffset + rect.top - 80;
            
            window.scrollTo({
              top: scrollTop,
              behavior: 'auto'
            });
            
            setTimeout(() => {
              window.scrollTo({
                top: scrollTop,
                behavior: 'auto'
              });
            }, 100);
          }
        }, 300);
      }
    };
    
    // 添加消息事件监听器
    window.addEventListener('message', handleMessage);
    
    // 清理函数
    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, []);

  // 生成推荐测试码
  const handleGenerateReferralCodes = async () => {
    setIsGeneratingCodes(true);
    try {
      const codes = await generateReferralTestCodes();
      setReferralCodes(codes);
      setShowReferralButton(false);
    } catch (error) {
      console.error('生成推荐测试码失败:', error);
      alert('生成推荐测试码失败，请稍后重试');
    } finally {
      setIsGeneratingCodes(false);
    }
  };

  // 复制测试码
  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      alert('测试码已复制到剪贴板');
    } catch (error) {
      console.error('复制失败:', error);
      alert('复制失败，请手动复制');
    }
  };

  if (!state) {
    return (
      <div className="min-h-screen bg-[#3A7D78] py-8 px-4 text-white">
        <div className="text-center">
          <h1 className="text-2xl mb-4">无法访问分析报告</h1>
          <p className="mb-4">请先完成情感选择测试</p>
          <button
            onClick={() => navigate('/')}
            className="bg-[#F5B6C1] text-white px-6 py-2 rounded-lg hover:bg-[#F5B6C1]/90 transition-colors"
          >
            返回首页
          </button>
        </div>
      </div>
    );
  }

  const { leastEmotions, mostEmotions, analysisReport: initialReport } = state;
  
  // 确定显示的报告内容
  // 无论是否处于流式状态，都优先显示流式内容，这样即使isStreaming为false，也能继续接收和显示流式内容
  const displayContent = streamContent || initialReport;

  // 按维度对情感词进行分组
  const groupEmotionsByDimension = (emotions: Emotion[]) => {
    return emotions.reduce((acc, emotion) => {
      if (!acc[emotion.dimension]) {
        acc[emotion.dimension] = [];
      }
      acc[emotion.dimension].push(emotion);
      return acc;
    }, {} as Record<string, Emotion[]>);
  };

  const leastEmotionsByDimension = groupEmotionsByDimension(leastEmotions);
  const mostEmotionsByDimension = groupEmotionsByDimension(mostEmotions);

  return (
    <div className="min-h-screen bg-[#3A7D78] py-8 px-4">
      {/* 顶部导航栏 */}
      <div className="flex justify-end mb-8">
        <button
          onClick={() => navigate('/')}
          className="text-white hover:text-[#F5B6C1] transition-colors"
        >
          更多测评
        </button>
      </div>

      {/* 主要内容区域 */}
      <div className="max-w-4xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white/10 rounded-2xl p-8 border border-white/20 shadow-[0_8px_32px_rgba(0,0,0,0.1)]"
        >
          {/* 标题区 */}
          <div className="text-center mb-12">
            <h1 className="text-4xl font-bold text-white">请检视你的情绪记忆，并查收来自大语魔的翻译之书</h1>
          </div>

          {/* 情感词选择结果 */}
          <div className="mb-8 grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* 最无感的情感词 */}
            <div>
              <h3 className="text-lg font-medium text-white/90 mb-4">你少有的情绪体验</h3>
              <div className="grid grid-cols-1 gap-6">
                {Object.entries(leastEmotionsByDimension).map(([dimension, emotions]) => (
                  <div key={dimension} className="bg-white/5 rounded-lg p-4">
                    <h4 className="text-white/80 font-medium mb-2">{dimension}维度</h4>
                    <div className="flex flex-wrap gap-2">
                      {emotions.map(emotion => (
                        <span key={emotion.word} className="bg-[#94A3B8]/30 text-white/80 px-3 py-1 rounded-full text-sm">
                          {emotion.word}
                        </span>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* 最有感的情感词 */}
            <div>
              <h3 className="text-lg font-medium text-white/90 mb-4">你常见的情绪体验</h3>
              <div className="grid grid-cols-1 gap-6">
                {Object.entries(mostEmotionsByDimension).map(([dimension, emotions]) => (
                  <div key={dimension} className="bg-white/5 rounded-lg p-4">
                    <h4 className="text-white/80 font-medium mb-2">{dimension}维度</h4>
                    <div className="flex flex-wrap gap-2">
                      {emotions.map(emotion => (
                        <span key={emotion.word} className="bg-[#F5B6C1]/30 text-white/80 px-3 py-1 rounded-full text-sm">
                          {emotion.word}
                        </span>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* 流式生成提示 - 已移除 */}
          
          {/* 分析报告文本 - 使用龙藏体手写字体和纸张效果 */}
          <div className="mt-8 letter-paper">
            <div className="letter-content longcang-font text-[#333333]">
              
              {/* 移除了样式选择器 */}

              <div className="text-right mb-2 mt-5">
                <div className="inline-block">
                  <div className="mb-0 -mb-3">
                    <span className="greek-font text-xl text-[#B88A9E]" style={{ letterSpacing: '0.1em' }}>Ἡ βίβλος τῆς ἑρμηνείας τοῦ μεγάλου λόγου δαίμονος</span>
                  </div>
                  <div className="mb-0 -mb-3">
                    <span className="latin-font text-xl text-[#B88A9E]" style={{ letterSpacing: '0.05em' }}>Liber Interpretationum Daemonis Linguae Magni</span>
                  </div>
                  <div>
                    <span className="longcang-font text-xl text-[#B88A9E]" style={{ letterSpacing: '0.05em' }}>大语魔的翻译之书</span>
                  </div>
                </div>
              </div>
              
              {/* 空间占位，保持布局一致 */}
              <div className="mb-16"></div>
              
              <div className="overflow-visible">
                {/* 只有在有内容可显示时才渲染ReactMarkdown */}
                {displayContent && (
                  <div className="relative">
                    {/* 添加模糊效果提示 - 使用更有设计感的动画效果 */}
                    {isStreaming && (
                      <div className="absolute top-0 left-0 w-full h-full pointer-events-none z-10 overflow-hidden">
                        <div 
                          className="flex flex-col items-center justify-center mx-auto"
                          style={{
                            position: 'relative',
                            top: '30%',
                          }}
                        >
                          <motion.div
                            animate={{ scale: [1, 1.1, 1] }}
                            transition={{ 
                              repeat: Infinity, 
                              duration: 2,
                              ease: "easeInOut" 
                            }}
                            style={{
                              background: 'linear-gradient(45deg, rgba(214,122,156,0.1) 0%, rgba(214,122,156,0.2) 100%)',
                              borderRadius: '50%',
                              padding: '4px',
                              marginBottom: '4px'
                            }}
                          >
                            <Brain 
                              size={20} 
                              color="#B88A9E" 
                            />
                          </motion.div>
                          <div className="text-center font-sans font-light text-[#B88A9E]" style={{ fontSize: '0.9rem' }}>
                            大语魔正在整理自己的脑子
                          </div>
                        </div>
                      </div>
                    )}
                    <div className={`transition-all duration-500 ${isStreaming ? 'filter blur-sm' : ''}`}>
                      <ReactMarkdown components={{
                        p: ({node, children, ...props}) => children ? <p className="mb-[25px] text-lg leading-[36px] kaishu-font text-[#4A4A4A]" {...props}>{children}</p> : null,
                        h1: ({node, children, ...props}) => children ? <h1 className="text-3xl font-bold mb-[25px] leading-[45px] kaishu-font text-[#3A3A3A]" style={{fontWeight: 700}} {...props}>{children}</h1> : null,
                        h2: ({node, children, ...props}) => children ? <h2 className="text-2xl font-bold mb-[25px] leading-[40px] kaishu-font text-[#3A3A3A]" style={{fontWeight: 700}} {...props}>{children}</h2> : null,
                        h3: ({node, children, ...props}) => children ? <h3 className="text-xl font-bold mb-[25px] leading-[36px] kaishu-font text-[#3A3A3A]" style={{fontWeight: 700}} {...props}>{children}</h3> : null,
                        ul: ({node, children, ...props}) => children ? <ul className="list-disc list-inside mb-[25px] text-lg leading-[36px] kaishu-font text-[#4A4A4A]" {...props}>{children}</ul> : null,
                        ol: ({node, children, ...props}) => children ? <ol className="list-decimal list-inside mb-[25px] text-lg leading-[36px] kaishu-font text-[#4A4A4A]" {...props}>{children}</ol> : null,
                        li: ({node, children, ...props}) => children ? <li className="mb-[25px] kaishu-font" {...props}>{children}</li> : null,
                        strong: ({node, children, ...props}) => children ? <strong className="kaishu-font text-[#3A3A3A]" style={{fontWeight: 700}} {...props}>{children}</strong> : null,
                        em: ({node, children, ...props}) => children ? <em className="italic kaishu-font text-[#4A4A4A]" {...props}>{children}</em> : null
                      }}>
                      {displayContent}
                    </ReactMarkdown>
                    
                    {/* 打字光标效果，仅在流式生成时且显示内容时显示 */}
                    {isStreaming && showCursor && (
                      <span className="inline-block h-5 w-0.5 bg-[#4A4A4A] ml-1 animate-pulse"></span>
                    )}
                  </div>
                  </div>
                )}
                
                {/* 删除了"正在生成译书..."的提示文案 */}
              </div>
              
              <div className="flex justify-between mt-8 items-center">
                <div className="flex flex-col gap-2">
                  <button
                    onClick={() => {
                      // 复制当前显示的内容
                      navigator.clipboard.writeText(displayContent);
                      alert('译书内容已复制到剪贴板');
                    }}
                    // 只有在有内容时才启用按钮
                    disabled={!displayContent}
                    className={`px-3 py-1.5 rounded-lg text-sm transition-colors flex items-center gap-1 ${
                      !displayContent 
                        ? 'bg-[#B88A9E]/5 text-[#B88A9E]/50 cursor-not-allowed'
                        : 'bg-[#B88A9E]/10 hover:bg-[#B88A9E]/20 text-[#B88A9E] cursor-pointer'
                    }`}
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
                      <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
                    </svg>
                    复制
                  </button>
                  
                  {/* 推荐按钮 */}
                  {showReferralButton && (
                    <button
                      onClick={handleGenerateReferralCodes}
                      disabled={isGeneratingCodes}
                      className={`px-3 py-1.5 rounded-lg text-sm transition-colors flex items-center gap-1 ${
                        isGeneratingCodes
                          ? 'bg-[#B88A9E]/5 text-[#B88A9E]/50 cursor-not-allowed'
                          : 'bg-[#B88A9E]/10 hover:bg-[#B88A9E]/20 text-[#B88A9E] cursor-pointer'
                      }`}
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"></path>
                        <circle cx="9" cy="7" r="4"></circle>
                        <path d="M22 21v-2a4 4 0 0 0-3-3.87"></path>
                        <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
                      </svg>
                      {isGeneratingCodes ? '生成中...' : '推荐给朋友'}
                    </button>
                  )}
                  
                  {/* 显示生成的推荐测试码 */}
                  {referralCodes.length > 0 && (
                    <div className="mt-2 bg-[#B88A9E]/5 p-3 rounded-lg max-w-xs">
                      <h3 className="text-sm font-medium text-[#B88A9E] mb-2">赠送测试码给朋友</h3>
                      <div className="space-y-2">
                        {referralCodes.map((code, index) => (
                          <div key={index} className="flex items-center justify-between bg-white/50 p-2 rounded border">
                            <span className="text-sm font-mono text-[#4A4A4A]">{code}</span>
                            <button
                              onClick={() => copyToClipboard(code)}
                              className="ml-2 px-2 py-1 text-xs bg-[#B88A9E]/10 hover:bg-[#B88A9E]/20 text-[#B88A9E] rounded transition-colors"
                            >
                              复制
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
                
                {/* 降低不透明度，使其看起来像是印在纸上 */}
                <motion.div
                  animate={{ scale: [1, 1.1, 1] }}
                  transition={{ 
                    repeat: Infinity, 
                    duration: 2,
                    ease: "easeInOut" 
                  }}
                  className="opacity-80"
                  style={{
                    background: 'linear-gradient(45deg, rgba(214,122,156,0.1) 0%, rgba(214,122,156,0.2) 100%)',
                    borderRadius: '50%',
                    padding: '8px'
                  }}
                >
                  <Brain 
                    size={60} 
                    color="#B88A9E" 
                  />
                </motion.div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default AnalysisReport;